import React from 'react';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <span className="text-3xl">🐾</span>
              <img 
                src="https://pub-b9622d81b0bd442fb020f880d8cec37d.r2.dev/petrecord-logo.png" 
                alt="PetRecord.ai" 
                className="h-8"
              />
            </a>
            <a href="/" className="text-blue-600 hover:text-blue-800 font-medium">
              ← Back to Home
            </a>
          </div>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <p className="text-gray-600 mb-8">
            <strong>Last Updated:</strong> October 30, 2025<br />
            <strong>Entity:</strong> M1 Ventures dba PetRecord.ai ("PetRecord.ai", "we", "us", or "our")<br />
            <strong>Website:</strong> https://petrecord.ai
          </p>

          <p className="mb-6">
            This Privacy Policy governs our collection, use, disclosure and protection of information when you access or use https://petrecord.ai (the "Site") and our related services ("Services"). By using the Site or Services you agree to this Privacy Policy.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Personal Information We May Collect</h2>
          
          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.1 Information You Provide to Us</h3>
          <p className="mb-4">
            When you register an account or subscribe to our paid plan, we collect your name, email address, billing information, payment method (via Stripe), and any preferences you provide.
          </p>
          <p className="mb-4">
            If you contact us (e.g., support, feedback) we collect the information you send.
          </p>
          <p className="mb-6">
            If you upload your pet's veterinary records via our connected application (my.petrecord.ai), you supply the record files and metadata (pet name/species/breed, vet visit date, test results).
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.2 Information We Collect Automatically</h3>
          <p className="mb-4">
            Log and usage data: your IP address, browser type, operating system, referring URLs, pages visited, session duration, and interactions.
          </p>
          <p className="mb-6">
            Cookies and similar technologies to recognise your device and personalise your experience; third-party analytics may collect certain usage metrics.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.3 Derived, De-identified or Aggregate Information</h3>
          <p className="mb-6">
            We may extract metadata from uploaded records (e.g., date of vet visit) and generate AI-based summaries of those records. These samples may be de-identified or aggregated for analytics and feature improvement.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. How We Use Your Information</h2>
          <p className="mb-4">We may use Personal Information for the following purposes:</p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Provide, operate and maintain our Services, including processing your subscription and managing your account.</li>
            <li>Generate AI-summaries of your uploaded veterinary records and present those within the Service.</li>
            <li>Improve and customise our Services: feature development, analytics, testing, troubleshooting.</li>
            <li>Communicate with you: account notices, operational messages, promotional offers (if you opt-in).</li>
            <li>Protect against misuse, fraud, and ensure integrity, security and compliance.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. How We Share Your Information</h2>
          <p className="mb-4">We do not sell your Personal Information to third parties for direct marketing. We may share information as follows:</p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>With our service providers (e.g., Stripe for payment processing, Supabase for data storage, analytics platforms) who assist with operations and who agree to confidentiality and restricted use.</li>
            <li>With law enforcement or in response to lawful requests if required by law or to protect rights, property or safety.</li>
            <li>In connection with a merger, acquisition, or sale of all or part of our business; your information may be among transferred assets, and you will be notified if required.</li>
            <li>With your consent or at your direction (for example if you authorise sharing).</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Data Retention and Deletion</h2>
          <p className="mb-6">
            We retain your Personal Information for as long as necessary to fulfil the purposes outlined in this policy or as required by law. You may request deletion of your account and data by contacting help@petrecord.ai; once your account is terminated, we will delete or anonymise your data in accordance with our deletion policy, except where retention is required for legal or business purposes.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Security</h2>
          <p className="mb-6">
            We implement reasonable administrative, technical and physical safeguards to protect your information. However, no system is completely secure, so we cannot guarantee absolute protection of your data.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. International Users & Cross-Border Transfers</h2>
          <p className="mb-6">
            Our servers are located in the United States (Utah). By using our Services you agree that your information may be transferred to and processed in the United States and other jurisdictions.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Children's Privacy</h2>
          <p className="mb-6">
            Our Services are not intended for children under 13. We do not knowingly collect information from children under 13. If you believe we have collected such information, contact us at help@petrecord.ai and we will delete it.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Your Rights and Choices</h2>
          <p className="mb-4">Depending on your location (e.g., U.S. states), you may have rights including:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Access your Personal Information.</li>
            <li>Correct or update inaccurate information.</li>
            <li>Delete your Personal Information (account termination).</li>
            <li>Opt-out of marketing email communications by using the unsubscribe link or contacting us.</li>
          </ul>
          <p className="mb-6">
            If you are a resident of a state with privacy laws (such as California or Colorado), you may have additional rights (e.g., "Do Not Sell or Share My Personal Information"). We currently do not sell your personal data, but will update this policy if that changes.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Changes to this Privacy Policy</h2>
          <p className="mb-6">
            We may update this Privacy Policy from time to time. We will revise the "Last Updated" date at the top. Continued use of the Site after changes constitutes your acceptance of the revised policy.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Contact Information</h2>
          <p className="mb-4">If you have questions about this Privacy Policy, you may contact us at:</p>
          <p className="mb-6">
            M1 Ventures dba PetRecord.ai<br />
            help@petrecord.ai<br />
            <br />
            Address:<br />
            11757 W Ken Caryl Ave, #272, Littleton CO 80127
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;